import {ReactElement, useState} from "react";
import {
    AffordableRevenueSummaryItemModel,
    AffordableRevenueSummaryModel
} from "../../../../../__generated__/generated_types";
import {Property, propertyDisplayBudgetYear} from "../../../../../contexts/properties/logic";
import {Body, Cell, Head, HeaderRow, Row, Table} from "@zendeskgarden/react-tables";
import {buildAffordableCells, buildAffordableHeaderCells} from "./affordable_content_builders";
import * as css from "../../summary/revenueSummary.module.scss";
import tableCSS from "../../../../property-executive-summary/styles/table.module.scss";
import cn from "classnames";

import {getMonths} from "../../../../../utils/months-helper";
import {formatterDollarUSNoDecimal, formatterInt, formatterPercent} from "../../../../../utils/formatters";

type ExpandableKey = "rent" | "lossToLease" | "unitCount" | "expirationCount" | "occupiedCount" | "renewalCount" | "moveInCount" | "moveOutCount";

export interface IAffordableRevenueSummaryTableProps {
    summaryData: AffordableRevenueSummaryModel;
    property: Property;
}

export function AffordableRevenueSummaryTable(props: IAffordableRevenueSummaryTableProps): ReactElement {
    const {summaryData, property} = props;
    const [expanded, setExpanded] = useState<Record<ExpandableKey, boolean>>({
        rent: false,
        lossToLease: false,
        unitCount: false,
        expirationCount: false,
        occupiedCount: false,
        renewalCount: false,
        moveInCount: false,
        moveOutCount: false,
    });

    function handleExpand(key: ExpandableKey) {
        setExpanded(prev => ({
            ...prev,
            [key]: !prev[key],
        }));
    }

    const reforecastStartMonthIndex = property.reforecastStartMonthIndex;
    const budgetYear = propertyDisplayBudgetYear(property);
    const months = getMonths(property);

    function renderRow(label: string, item: AffordableRevenueSummaryModel[keyof AffordableRevenueSummaryModel], formatter: Intl.NumberFormat): ReactElement {
        if (typeof item !== "object" || !("reforecast" in item)) {
            return <></>;
        }
        return (
            <Row>
                <Cell className={cn(tableCSS.fixedCol, tableCSS.firstCol)}>{label}</Cell>
                {buildAffordableCells(reforecastStartMonthIndex, item.reforecast, item.budget, formatter)}
            </Row>
        );
    }

    function renderExpandableRow(
        label: string,
        key: ExpandableKey,
        parentItem: AffordableRevenueSummaryModel[keyof AffordableRevenueSummaryModel],
        children: { label: string; item: AffordableRevenueSummaryModel[keyof AffordableRevenueSummaryModel] }[],
        formatter: Intl.NumberFormat,
    ): ReactElement {
        if (typeof parentItem !== "object" || !("reforecast" in parentItem)) {
            return <></>;
        }
        return (
            <>
                <Row>
                    <Cell onClick={() => handleExpand(key)} className={cn(tableCSS.fixedCol, tableCSS.firstCol)}>
                        {expanded[key] ? "-" : "+"} {label}
                    </Cell>
                    {buildAffordableCells(reforecastStartMonthIndex, parentItem.reforecast, parentItem.budget, formatter)}
                </Row>
                {expanded[key] && children.map((child, i) => {
                    if (typeof child.item !== "object" || !("reforecast" in child.item)) {
                        return null;
                    }
                    return (
                        <Row key={i}>
                            <Cell className={cn(css.unitTypeName, tableCSS.fixedCol, tableCSS.firstCol)} style={{paddingLeft: "1.5rem"}}>{child.label}</Cell>
                            {buildAffordableCells(reforecastStartMonthIndex, child.item.reforecast, child.item.budget, formatter)}
                        </Row>
                    );
                })}
            </>
        );
    }

    function SubHead({title}: { title: string }): ReactElement {
        const colSpan = (12 - reforecastStartMonthIndex) + 12;
        return (
            <Row style={{backgroundColor: "var(--grey-200)"}}>
                <Cell
                    style={{
                        position: "sticky",
                        left: 0,
                        backgroundColor: "var(--grey-200)",
                    }}
                >
                    {title}
                </Cell>
                <Cell
                    colSpan={colSpan}
                    style={{backgroundColor: "var(--grey-200)"}}
                />
            </Row>
        );
    }

    function getLossToLeaseString(values: AffordableRevenueSummaryItemModel): string {
        let anyPositive = false;
        let anyNegative = false;
        for (const value of values.reforecast) {
            if (value > 0) {
                anyPositive = true;
            }
            if (value < 0) {
                anyNegative = true;
            }
        }
        for (const value of values.budget) {
            if (value > 0) {
                anyPositive = true;
            }
            if (value < 0) {
                anyNegative = true;
            }
        }

        if(anyPositive && anyNegative) {
            return "Loss/Gain to Lease";
        } else if (anyPositive) {
            return "Gain to Lease";
        } else {
            return "Loss to Lease";
        }
    }

    return (
        <div className={cn(tableCSS.wrapper, tableCSS.revTable)}>
            <div className={tableCSS.topHeader} />
            <div className={tableCSS.tableWrapper}>
                <Table className={tableCSS.table}>
                    <Head isSticky style={{zIndex: 3}}>
                        <HeaderRow className={tableCSS.headerRow}>
                            {buildAffordableHeaderCells(reforecastStartMonthIndex, budgetYear, months)}
                        </HeaderRow>
                    </Head>
                    <Body>
                        <SubHead title="Metrics" />
                        {renderExpandableRow(
                                "Unit Count",
                                "unitCount",
                                summaryData.unitCount,
                                summaryData.unitTypes.map(ut => {
                                    return {label: ut.unitTypeName, item: ut.unitCount};
                                }),
                                formatterInt
                        )}
                        {renderExpandableRow(
                                "Expiration Count",
                                "expirationCount",
                                summaryData.expirationCount,
                                summaryData.unitTypes.map(ut => {
                                    return {label: ut.unitTypeName, item: ut.expirationCount};
                                }),
                                formatterInt
                        )}
                        {renderRow("Occupied %", summaryData.occupiedRate, formatterPercent)}
                        {renderExpandableRow(
                                "Occupied Count",
                                "occupiedCount",
                                summaryData.occupiedCount,
                                summaryData.unitTypes.map(ut => {
                                    return {label: ut.unitTypeName, item: ut.occupiedCount};
                                }),
                                formatterInt
                        )}
                        {/*renderRow("Renewal %", summaryData.renewalRate, formatterPercent)*/}
                        {renderExpandableRow(
                                "Renewal Count",
                                "renewalCount",
                                summaryData.renewalCount,
                                summaryData.unitTypes.map(ut => {
                                    return {label: ut.unitTypeName, item: ut.renewalCount};
                                }),
                                formatterInt
                        )}
                        {renderExpandableRow(
                                "Move In Count",
                                "moveInCount",
                                summaryData.moveInCount,
                                summaryData.unitTypes.map(ut => {
                                    return {label: ut.unitTypeName, item: ut.moveInCount};
                                }),
                                formatterInt
                        )}
                        {/*renderRow("Move Out %", summaryData.moveOutRate, formatterPercent)*/}
                        {renderExpandableRow(
                                "Move Out Count",
                                "moveOutCount",
                                summaryData.moveOutCount,
                                summaryData.unitTypes.map(ut => {
                                    return {label: ut.unitTypeName, item: ut.moveOutCount};
                                }),
                                formatterInt
                        )}

                        <SubHead title="Amounts" />
                        {renderRow("Avg Market Rent", summaryData.averageMarketRent, formatterDollarUSNoDecimal)}
                        {renderRow("In Place Rent", summaryData.inPlaceRent, formatterDollarUSNoDecimal)}

                        <SubHead title="Financials" />
                        {renderExpandableRow(
                            "Market Rent",
                            "rent",
                            summaryData.rent,
                            [
                                {label: "Non-Subsidized Market Rent", item: summaryData.conventionalRent},
                                {label: "Subsidized Market Rent", item: summaryData.affordableRent},
                            ],
                            formatterDollarUSNoDecimal,
                        )}
                        {renderExpandableRow(
                            getLossToLeaseString(summaryData.lossToLease),
                            "lossToLease",
                            summaryData.lossToLease,
                            [
                                {label: `Non-Subsidized ${getLossToLeaseString(summaryData.conventionalLossToLease)}`, item: summaryData.conventionalLossToLease},
                                {label: `Subsidized ${getLossToLeaseString(summaryData.affordableLossToLease)}`, item: summaryData.affordableLossToLease},
                            ],
                            formatterDollarUSNoDecimal,
                        )}
                        {renderRow("Gross Potential Rent", summaryData.grossPotentialRent, formatterDollarUSNoDecimal)}
                    </Body>
                </Table>
            </div>
        </div>
    );
}
